import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const StopLossTargetCalculator = () => {
  const content = article.find((item) => item.id === 3);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    positionType: "long",
    entryPrice: "100.00",
    riskAmount: "100",
    rewardRiskRatio: "2",
    quantity: "100",
    brokerage: "5.00",
    taxes: "2.00",
    slippage: "0.50",
    currencyConversion: "1",
    trailingStop: "0",
    financingCost: "0.00",
    portfolioSize: "10000",
    riskPerTrade: "2",
    volatility: "15",
    atrPeriod: "14",
    atrValue: "2.50",
    supportLevel: "90.00",
    resistanceLevel: "130.00",
    timeHorizon: "30"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [priceLevels, setPriceLevels] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculatePrices();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculatePrices = () => {
    const {
      positionType,
      entryPrice,
      riskAmount,
      rewardRiskRatio,
      quantity,
      brokerage,
      taxes,
      slippage,
      currencyConversion,
      trailingStop,
      financingCost,
      portfolioSize,
      riskPerTrade,
      volatility,
      atrPeriod,
      atrValue,
      supportLevel,
      resistanceLevel,
      timeHorizon
    } = inputs;

    if (!entryPrice || !riskAmount || !rewardRiskRatio || !quantity) {
      return;
    }

    const entry = parseFloat(entryPrice);
    const risk = parseFloat(riskAmount);
    const rr = parseFloat(rewardRiskRatio);
    const qty = parseFloat(quantity);
    const fee = parseFloat(brokerage || 0) + parseFloat(taxes || 0);
    const slip = parseFloat(slippage || 0);
    const trail = parseFloat(trailingStop || 0) / 100;
    const finance = parseFloat(financingCost || 0);
    const curr = parseFloat(currencyConversion || 1);
    const portfolio = parseFloat(portfolioSize || 0);
    const riskPercent = parseFloat(riskPerTrade || 0);
    const vol = parseFloat(volatility || 15);
    const atr = parseFloat(atrValue || 0);
    const support = parseFloat(supportLevel || 0);
    const resistance = parseFloat(resistanceLevel || 0);
    const time = parseFloat(timeHorizon || 30);

    // Risk per share/unit
    const riskPerUnit = risk / qty;

    // Calculate stop-loss price
    let stopLossPrice;
    if (positionType === "long") {
      stopLossPrice = entry - riskPerUnit - slip;
      if (trail > 0) stopLossPrice = Math.max(stopLossPrice, entry * (1 - trail));
    } else {
      stopLossPrice = entry + riskPerUnit + slip;
      if (trail > 0) stopLossPrice = Math.min(stopLossPrice, entry * (1 + trail));
    }

    // Calculate target price using Reward/Risk ratio
    const targetDistance = riskPerUnit * rr;
    let targetPrice = positionType === "long"
      ? entry + targetDistance - slip
      : entry - targetDistance + slip;

    // Adjust for support/resistance levels
    if (positionType === "long") {
      if (resistance > 0 && targetPrice > resistance) {
        targetPrice = resistance - slip;
      }
      if (support > 0 && stopLossPrice < support) {
        stopLossPrice = support - slip;
      }
    } else {
      if (support > 0 && targetPrice < support) {
        targetPrice = support + slip;
      }
      if (resistance > 0 && stopLossPrice > resistance) {
        stopLossPrice = resistance + slip;
      }
    }

    // ATR-based calculations
    const atrStopLoss = atr > 0 ? 
      (positionType === "long" ? entry - (2 * atr) : entry + (2 * atr)) : 0;
    const atrTarget = atr > 0 ? 
      (positionType === "long" ? entry + (3 * atr) : entry - (3 * atr)) : 0;

    // Volatility-adjusted calculations
    const volAdjustment = vol / 15; // Normalize to 15% volatility
    const adjustedRiskPerUnit = riskPerUnit * volAdjustment;

    // Total Risk & Reward calculations
    const totalRisk = ((Math.abs(entry - stopLossPrice) * qty) + fee + finance) * curr;
    const totalReward = ((Math.abs(targetPrice - entry) * qty) - fee) * curr;

    // Position sizing based on portfolio
    const portfolioRiskAmount = portfolio * (riskPercent / 100);
    const calculatedQuantity = Math.floor(portfolioRiskAmount / riskPerUnit);

    // Risk percentage calculations
    const riskPercentage = (Math.abs(entry - stopLossPrice) / entry) * 100;
    const rewardPercentage = (Math.abs(targetPrice - entry) / entry) * 100;
    const actualRrRatio = rewardPercentage / riskPercentage;

    // Time-adjusted metrics
    const dailyReturn = (rewardPercentage / time) * 100;
    const annualizedReturn = dailyReturn * 365;

    // Generate price levels for visualization
    const levels = generatePriceLevels(entry, stopLossPrice, targetPrice, positionType);

    setResult({
      stopLossPrice: stopLossPrice,
      targetPrice: targetPrice,
      totalRisk: totalRisk,
      totalReward: totalReward,
      riskPerUnit: riskPerUnit,
      riskPercentage: riskPercentage,
      rewardPercentage: rewardPercentage,
      actualRrRatio: actualRrRatio,
      calculatedQuantity: calculatedQuantity,
      portfolioRiskAmount: portfolioRiskAmount,
      atrStopLoss: atrStopLoss,
      atrTarget: atrTarget,
      adjustedRiskPerUnit: adjustedRiskPerUnit,
      dailyReturn: dailyReturn,
      annualizedReturn: annualizedReturn,
      positionValue: entry * qty * curr,
      breakEvenPrice: positionType === "long" 
        ? entry + (fee / qty)
        : entry - (fee / qty)
    });

    setPriceLevels(levels);
  };

  const generatePriceLevels = (entry, stopLoss, target, positionType) => {
    const levels = [];
    const minPrice = Math.min(entry, stopLoss, target);
    const maxPrice = Math.max(entry, stopLoss, target);
    const range = maxPrice - minPrice;
    
    // Add key levels
    levels.push({ price: entry, type: 'entry', label: 'Entry Price' });
    levels.push({ price: stopLoss, type: 'stopLoss', label: 'Stop Loss' });
    levels.push({ price: target, type: 'target', label: 'Target Price' });
    
    // Add percentage levels
    for (let i = 1; i <= 4; i++) {
      const percent = i * 0.25;
      if (positionType === 'long') {
        const levelPrice = entry + (target - entry) * percent;
        levels.push({ price: levelPrice, type: 'partial', label: `${percent * 100}% Profit` });
      } else {
        const levelPrice = entry - (entry - target) * percent;
        levels.push({ price: levelPrice, type: 'partial', label: `${percent * 100}% Profit` });
      }
    }
    
    return levels.sort((a, b) => a.price - b.price);
  };

  const calculateRiskMetrics = () => {
    const { portfolioSize, riskPerTrade, volatility, timeHorizon } = inputs;
    
    if (!portfolioSize || !riskPerTrade) return null;

    const portfolio = parseFloat(portfolioSize);
    const riskPercent = parseFloat(riskPerTrade);
    const vol = parseFloat(volatility || 15);
    const time = parseFloat(timeHorizon || 30);

    const riskPerTradeAmount = portfolio * (riskPercent / 100);
    const dailyVar = portfolio * (vol / 100) / Math.sqrt(252);
    const maxDrawdown = portfolio * 0.1;
    const timeAdjustedRisk = riskPerTradeAmount * Math.sqrt(time / 30);

    return {
      riskPerTradeAmount,
      dailyVar,
      maxDrawdown,
      timeAdjustedRisk,
      suggestedPositionSize: Math.floor(riskPerTradeAmount / (portfolio * 0.02))
    };
  };

  const resetCalculator = () => {
    setInputs({
      positionType: "long",
      entryPrice: "100.00",
      riskAmount: "100",
      rewardRiskRatio: "2",
      quantity: "100",
      brokerage: "5.00",
      taxes: "2.00",
      slippage: "0.50",
      currencyConversion: "1",
      trailingStop: "0",
      financingCost: "0.00",
      portfolioSize: "10000",
      riskPerTrade: "2",
      volatility: "15",
      atrPeriod: "14",
      atrValue: "2.50",
      supportLevel: "90.00",
      resistanceLevel: "130.00",
      timeHorizon: "30"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Stop-Loss Price,${result.stopLossPrice.toFixed(2)}`,
      `Target Price,${result.targetPrice.toFixed(2)}`,
      `Risk Percentage,${result.riskPercentage.toFixed(2)}%`,
      `Reward Percentage,${result.rewardPercentage.toFixed(2)}%`,
      `Actual R:R Ratio,${result.actualRrRatio.toFixed(2)}`,
      `Total Risk,${result.totalRisk.toFixed(2)}`,
      `Total Reward,${result.totalReward.toFixed(2)}`,
      `Risk per Unit,${result.riskPerUnit.toFixed(2)}`,
      `Calculated Quantity,${result.calculatedQuantity}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stop-loss-target-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        rewardRiskRatio: "3", 
        riskPerTrade: "1",
        trailingStop: "5"
      },
      moderate: { 
        rewardRiskRatio: "2", 
        riskPerTrade: "2",
        trailingStop: "8"
      },
      aggressive: { 
        rewardRiskRatio: "1.5", 
        riskPerTrade: "3",
        trailingStop: "12"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} trading preset applied`, "info");
  };

  // Fixed Chart Data
  const priceChartData = result ? {
    labels: priceLevels.map(level => level.label),
    datasets: [{
      label: 'Price Levels',
      data: priceLevels.map(level => level.price),
      backgroundColor: priceLevels.map(level => 
        level.type === 'entry' ? '#4ECDC4' :
        level.type === 'stopLoss' ? '#FF6B6B' :
        level.type === 'target' ? '#28A745' : '#FFD93D'
      ),
      borderWidth: 2
    }]
  } : null;

  const riskMetrics = calculateRiskMetrics();

  return (
    <div className="profit-calculator">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stop-Loss & Target Price Calculator</h1>
        </div>
        <p>Advanced price level calculations with risk management and technical analysis</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.actualRrRatio >= 2 ? 'positive' : 'negative'}`}>
                        SL: ${calc.result.stopLossPrice.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Price Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "levels" ? "active" : ""}`}
                onClick={() => setActiveTab("levels")}
              >
                <i className="fas fa-layer-group"></i>
                {isMobile ? 'Levels' : 'Price Levels'}
              </button>
              <button 
                className={`tab ${activeTab === "risk" ? "active" : ""}`}
                onClick={() => setActiveTab("risk")}
              >
                <i className="fas fa-shield-alt"></i>
                {isMobile ? 'Risk' : 'Risk Management'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Trading Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-bullseye"></i>
                    Trade Parameters
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-up-down"></i> Position Type</label>
                      <select name="positionType" value={inputs.positionType} onChange={handleChange}>
                        <option value="long">Long</option>
                        <option value="short">Short</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Amount ($)</label>
                      <input 
                        type="number" 
                        name="riskAmount" 
                        value={inputs.riskAmount} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-balance-scale"></i> Reward/Risk Ratio</label>
                      <input 
                        type="number" 
                        name="rewardRiskRatio" 
                        value={inputs.rewardRiskRatio} 
                        onChange={handleChange}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                      <input 
                        type="number" 
                        name="quantity" 
                        value={inputs.quantity} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Time Horizon (Days)</label>
                      <input 
                        type="number" 
                        name="timeHorizon" 
                        value={inputs.timeHorizon} 
                        onChange={handleChange}
                        placeholder="30"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Cost & Technical Levels
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Brokerage Fees</label>
                      <input 
                        type="number" 
                        name="brokerage" 
                        value={inputs.brokerage} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Taxes</label>
                      <input 
                        type="number" 
                        name="taxes" 
                        value={inputs.taxes} 
                        onChange={handleChange}
                        placeholder="2.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Slippage</label>
                      <input 
                        type="number" 
                        name="slippage" 
                        value={inputs.slippage} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Support Level</label>
                      <input 
                        type="number" 
                        name="supportLevel" 
                        value={inputs.supportLevel} 
                        onChange={handleChange}
                        placeholder="90.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Resistance Level</label>
                      <input 
                        type="number" 
                        name="resistanceLevel" 
                        value={inputs.resistanceLevel} 
                        onChange={handleChange}
                        placeholder="130.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currencyConversion" 
                        value={inputs.currencyConversion} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Price Levels Tab */}
          {activeTab === "levels" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-wave-square"></i>
                  Volatility & ATR Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Volatility (%)</label>
                    <input 
                      type="number" 
                      name="volatility" 
                      value={inputs.volatility} 
                      onChange={handleChange}
                      placeholder="15"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-ruler"></i> ATR Period</label>
                    <input 
                      type="number" 
                      name="atrPeriod" 
                      value={inputs.atrPeriod} 
                      onChange={handleChange}
                      placeholder="14"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-ruler-horizontal"></i> ATR Value</label>
                    <input 
                      type="number" 
                      name="atrValue" 
                      value={inputs.atrValue} 
                      onChange={handleChange}
                      placeholder="2.50"
                      step="0.01"
                    />
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-trailer"></i>
                  Advanced Stop Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Trailing Stop (%)</label>
                    <input 
                      type="number" 
                      name="trailingStop" 
                      value={inputs.trailingStop} 
                      onChange={handleChange}
                      placeholder="0"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-hand-holding-usd"></i> Financing Cost</label>
                    <input 
                      type="number" 
                      name="financingCost" 
                      value={inputs.financingCost} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div><br/>

                {result && result.atrStopLoss > 0 && (
                  <div className="risk-results">
                    <h4>ATR-Based Calculations</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">ATR Stop Loss:</span>
                        <span className="metric-value">${result.atrStopLoss.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">ATR Target:</span>
                        <span className="metric-value">${result.atrTarget.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Vol-Adjusted Risk:</span>
                        <span className="metric-value">${result.adjustedRiskPerUnit.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Risk Management Tab */}
          {activeTab === "risk" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-shield-alt"></i>
                  Portfolio Risk Management
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Portfolio Size ($)</label>
                    <input 
                      type="number" 
                      name="portfolioSize" 
                      value={inputs.portfolioSize} 
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Risk Per Trade (%)</label>
                    <input 
                      type="number" 
                      name="riskPerTrade" 
                      value={inputs.riskPerTrade} 
                      onChange={handleChange}
                      placeholder="2"
                      step="0.1"
                    />
                  </div><br/>
                </div>

                {riskMetrics && (
                  <div className="risk-results">
                    <h4>Portfolio Risk Metrics</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">Risk per Trade:</span>
                        <span className="metric-value">${riskMetrics.riskPerTradeAmount.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Daily VaR:</span>
                        <span className="metric-value">${riskMetrics.dailyVar.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Max Drawdown:</span>
                        <span className="metric-value">${riskMetrics.maxDrawdown.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Time-Adjusted Risk:</span>
                        <span className="metric-value">${riskMetrics.timeAdjustedRisk.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.result.actualRrRatio >= 2 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.result.actualRrRatio >= 2 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          R:R {calc.result.actualRrRatio.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-arrow-down"></i> SL: ${calc.result.stopLossPrice.toFixed(2)}</span>
                        <span><i className="fas fa-bullseye"></i> Target: ${calc.result.targetPrice.toFixed(2)}</span>
                        <span><i className="fas fa-percentage"></i> Risk: {calc.result.riskPercentage.toFixed(2)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "levels" || activeTab === "risk") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-bullseye"></i>
                    Price Level Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value">
                          ${result.stopLossPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-arrow-down"></i>
                          Stop-Loss Price
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          ${result.targetPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Target Price
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.actualRrRatio.toFixed(2)}:1
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Actual R:R Ratio
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.calculatedQuantity}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-ruler"></i>
                          Optimal Quantity
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Risk Percentage:</span>
                        <span>{result.riskPercentage.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Reward Percentage:</span>
                        <span>{result.rewardPercentage.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Risk per Unit:</span>
                        <span>${result.riskPerUnit.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-exclamation-triangle"></i> Total Risk:</span>
                        <span>${result.totalRisk.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Total Reward:</span>
                        <span>${result.totalReward.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Daily Return:</span>
                        <span>{result.dailyReturn.toFixed(2)}%</span>
                      </div>
                    </div>

                    {/* Risk Assessment */}
                    <div className="risk-summary">
                      <h4>Trade Assessment</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.riskPercentage < 2 ? 'positive' : result.riskPercentage < 5 ? 'moderate' : 'negative'}`}>
                            {result.riskPercentage < 2 ? 'Low' : result.riskPercentage < 5 ? 'Medium' : 'High'}
                          </div>
                          <div className="risk-label">Risk Level</div>
                        </div>
                        <div className="risk-card">
                          <div className={`risk-value ${result.actualRrRatio >= 2 ? 'positive' : result.actualRrRatio >= 1 ? 'moderate' : 'negative'}`}>
                            {result.actualRrRatio >= 2 ? 'Good' : result.actualRrRatio >= 1 ? 'Fair' : 'Poor'}
                          </div>
                          <div className="risk-label">R/R Quality</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart - FIXED TOOLTIP */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Price Levels Visualization
                      </h3>
                      <Bar 
                        data={priceChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          indexAxis: 'y',
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const value = context.parsed.x;
                                  if (typeof value === 'number' && !isNaN(value)) {
                                    return `$${value.toFixed(2)}`;
                                  }
                                  return `$${0}`;
                                }
                              }
                            }
                          },
                          scales: {
                            x: {
                              beginAtZero: false,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toFixed(2);
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-bullseye fa-3x"></i>
                    <p>Enter your trade parameters to calculate price levels</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "levels" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-layer-group"></i>
                  Advanced Price Levels
                </h3>
                
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.breakEvenPrice.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Break-even Price
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.positionValue.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Position Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.annualizedReturn.toFixed(2)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Annualized Return
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.portfolioRiskAmount.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-shield-alt"></i>
                      Portfolio Risk
                    </div>
                  </div>
                </div><br/>

                <div className="risk-results-panel">
                  <h3>
                    <i className="fas fa-analytics"></i>
                    Technical Analysis
                  </h3>
                  <div className="risk-visualization">
                    <div className="position-breakdown">
                      <h4>Price Level Summary</h4>
                      {priceLevels.map((level, index) => (
                        <div key={index} className="breakdown-item">
                          <span>{level.label}:</span>
                          <span className={`${level.type === 'stopLoss' ? 'negative' : level.type === 'target' ? 'positive' : ''}`}>
                            ${level.price.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "risk" && riskMetrics && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-shield-alt"></i>
                  Risk Management Summary
                </h3><br/>
                
                <div className="position-breakdown">
                  <h4>Portfolio Protection</h4>
                  <div className="breakdown-item">
                    <span>Maximum Position Size:</span>
                    <span>{riskMetrics.suggestedPositionSize} shares</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Risk per Trade Amount:</span>
                    <span>${riskMetrics.riskPerTradeAmount.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Daily Value at Risk:</span>
                    <span>${riskMetrics.dailyVar.toFixed(2)}</span>
                  </div>
                  <div className="breakdown-item">
                    <span>Maximum Drawdown Limit:</span>
                    <span>${riskMetrics.maxDrawdown.toFixed(2)}</span>
                  </div>
                </div><br/>

                <div className="risk-meter">
                  <div className="meter-label">Portfolio Risk Level</div>
                  <div className="meter-bar">
                    <div 
                      className="meter-fill"
                      style={{ width: `${Math.min((parseFloat(inputs.riskPerTrade) / 5) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <div className="meter-value">{inputs.riskPerTrade}% per trade</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StopLossTargetCalculator;